/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Input, InputNumber, Button, Select, Switch, Card, Row, Col, message } from 'antd';
import { medicationService, medicationCategoryService, medicationUnitService } from '../../../services/pharmacyService';
import Breadcrumb from '../../../components/ui/Breadcrumb';

const AddMedication = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [form] = Form.useForm();
  const medicationId = searchParams.get('id');
  const isEdit = Boolean(medicationId);

  const fetchData = useCallback(async () => {
    try {
      const [catResponse, unitResponse] = await Promise.all([
        medicationCategoryService.getAll({ per_page: 100, is_active: true }),
        medicationUnitService.getAll({ per_page: 100, is_active: true })
      ]);
      setCategories(catResponse.data || []);
      setUnits(unitResponse || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Error al cargar datos');
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (isEdit) {
      loadMedication();
    }
  }, [isEdit, medicationId]);

  const loadMedication = async () => {
    try {
      const medication = await medicationService.getById(parseInt(medicationId));
      form.setFieldsValue({
        name: medication.name,
        code: medication.code,
        active_ingredient: medication.active_ingredient,
        category_id: medication.category_id,
        unit_id: medication.unit_id,
        description: medication.description,
        presentation: medication.presentation,
        concentration: medication.concentration,
        concentration_unit: medication.concentration_unit,
        min_stock: medication.min_stock,
        current_stock: medication.current_stock,
        requires_prescription: medication.requires_prescription,
        is_active: medication.is_active
      });
    } catch (error) {
      message.error('Error al cargar medicamento');
      navigate('/medications');
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      if (isEdit) {
        await medicationService.update(parseInt(medicationId), values);
        message.success('Medicamento actualizado correctamente');
      } else {
        await medicationService.create(values);
        message.success('Medicamento creado correctamente');
      }
      navigate('/medications');
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error al guardar medicamento';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title">{isEdit ? 'Editar' : 'Agregar'} Medicamento</h3>
                <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Medicamentos', to: '/medications' }, { label: isEdit ? 'Editar' : 'Agregar' }]} />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <Card>
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={onFinish}
                  initialValues={{
                    is_active: true,
                    requires_prescription: false,
                    min_stock: 0,
                    current_stock: 0
                  }}
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Nombre"
                        name="name"
                        rules={[{ required: true, message: 'El nombre es requerido' }]}
                      >
                        <Input placeholder="Nombre del medicamento" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Código"
                        name="code"
                        rules={[{ required: true, message: 'El código es requerido' }]}
                      >
                        <Input placeholder="Código único" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Principio Activo"
                        name="active_ingredient"
                        rules={[{ required: true, message: 'El principio activo es requerido' }]}
                      >
                        <Input placeholder="Sustancia activa" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Categoría"
                        name="category_id"
                        rules={[{ required: true, message: 'La categoría es requerida' }]}
                      >
                        <Select
                          placeholder="Seleccione una categoría"
                          showSearch
                          optionFilterProp="children"
                        >
                          {categories.map(c => (
                            <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        label="Unidad de Medida"
                        name="unit_id"
                        rules={[{ required: true, message: 'La unidad es requerida' }]}
                      >
                        <Select
                          placeholder="Seleccione unidad"
                          showSearch
                          optionFilterProp="children"
                        >
                          {units.map(u => (
                            <Select.Option key={u.id} value={u.id}>{u.name} ({u.abbreviation})</Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="Presentación"
                        name="presentation"
                      >
                        <Input placeholder="Ej: Tableta, Jarabe" />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item
                        label="Concentración"
                        name="concentration"
                      >
                        <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="0.00" />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item
                        label="Unidad Conc."
                        name="concentration_unit"
                      >
                        <Input placeholder="mg, ml" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        label="Stock Mínimo"
                        name="min_stock"
                      >
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="Stock Actual"
                        name="current_stock"
                      >
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="Requiere Receta"
                        name="requires_prescription"
                        valuePropName="checked"
                      >
                        <Switch checkedChildren="Sí" unCheckedChildren="No" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label="Descripción"
                    name="description"
                  >
                    <Input.TextArea rows={2} placeholder="Descripción adicional" />
                  </Form.Item>

                  <Form.Item
                    label="Estado"
                    name="is_active"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
                  </Form.Item>

                  <Form.Item className="text-end">
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      className="me-2"
                    >
                      {isEdit ? 'Actualizar' : 'Guardar'}
                    </Button>
                    <Button onClick={() => navigate('/medications')}>
                      Cancelar
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </div>
          </div>
    </>
  );
};

export default AddMedication;
